import moment from 'moment-timezone'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, ref } from 'vue'

import api from '@/api.js'
import i18n from '@/i18n.js'
import router from '@/router'

import { useAuthStore } from './auth.js'

type WorkingTime = {
  end: string
  timezone: string
}

type Config = {
  printers: any[]
  organizationConfig: Record<string, any>
  workingTime: WorkingTime
  language: string
  featureToggles: string[]
  [key: string]: any
}

type BillingStatus = {
  stillInTrial?: boolean
  pendingCardMigration?: boolean
  [key: string]: any
}

export const useConfigStore = defineStore('config', () => {
  const config = ref<Config>({
    printers: [],
    organizationConfig: {},
    workingTime: {
      end: '',
      timezone: ''
    },
    featureToggles: [],
    language: 'ES'
  })
  const billingStatus = ref<BillingStatus>({})

  async function updateConfig(newConfig: Config) {
    await api.put('/dashboard/config', newConfig)
    config.value = newConfig
    await refreshConfig()
  }

  async function refreshConfig() {
    const response = await api.get('/dashboard/config')
    const newConfig = response.data
    updateLanguage(newConfig.language)
    if (newConfig.workingTime?.timezone) {
      moment.tz.setDefault(newConfig.workingTime.timezone)
    }
    config.value = newConfig
  }

  async function refreshBillingStatus() {
    try {
      const response = await api.get('/dashboard/billing/status')
      billingStatus.value = response.data
    } catch {
      billingStatus.value = {}
    }
  }

  function updateLanguage(defaultLanguage?: string) {
    if (router.currentRoute.value.query.translate === 'true') {
      i18n.global.locale.value = '' as any
      i18n.global.fallbackLocale.value = '' as any
    } else {
      const auth = useAuthStore()
      const userLanguage = auth.rootUser.language
      i18n.global.locale.value = (userLanguage ||
        defaultLanguage ||
        window.navigator.language) as any
    }
  }

  const usesTBAI = computed(() => {
    const { countryCode, regionCode } = config.value.legalEntity || {}
    return countryCode === 'ES' && regionCode === 'PV'
  })

  return {
    config,
    billingStatus,
    updateConfig,
    refreshConfig,
    refreshBillingStatus,
    usesTBAI
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useConfigStore, import.meta.hot))
}
